import { ErrorMessage, Field, Form, Formik } from "formik";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { registerAccount, registerUser } from "../../features/user/userSlice";
import { base_url } from "../../utils/axiosConfig";

const MemberSignUp = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);
  const [packages, setPackages] = useState([]);
  const [referredUserData, setReferredUserData] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const timeoutRef = useRef(null);

  // Debounce function for referral code validation
  const debounce = (func, delay) => {
    return (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch referred user data
  const fetchReferredUserData = async (referralCode) => {
    if (!referralCode) {
      setReferredUserData(null);
      return;
    }
    try {
      const response = await axios.get(
        `${base_url}user/getReferredUserData?referralCode=${referralCode}`
      );
      setReferredUserData(response.data);
    } catch (error) {
      console.error("Error fetching referred user data:", error);
      setReferredUserData(null);
    }
  };

  const debouncedFetchReferredUserData = debounce(fetchReferredUserData, 500);

  // Fetch packages for the commented-out package selection step
  useEffect(() => {
    if (currentStep === 3 && show) {
      axios
        .get(`${base_url}v1/package/fetchAllpackage`)
        .then((response) => {
          setPackages(response.data.data);
        })
        .catch((error) => {
          console.error("Error fetching packages:", error);
        });
    }
  }, [currentStep, show]);

  // Cleanup debounce timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Calculate max date for 18+ validation (today minus 18 years)
  const maxDate18 = new Date();
  maxDate18.setFullYear(maxDate18.getFullYear() - 18);

  // Validation schemas for each step
  const signupSchemas = [
    Yup.object({
      firstname: Yup.string().required("First Name is required"),
      lastname: Yup.string().required("Last Name is required"),
      dob: Yup.date()
        .required("DOB is required")
        .max(maxDate18, "You must be 18+ years of age"),
      gender: Yup.string().required("Gender is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      mobile: Yup.string()
        .matches(/^[0-9]+$/, "Not a Valid Phone Number")
        .required("Phone is required"),
      profilePhoto: Yup.mixed().required("Profile photo is required"),
      address: Yup.object({
        address_line: Yup.string().required("Street address is required"),
        city: Yup.string().required("City is required"),
        state: Yup.string().required("State is required"),
        country: Yup.string().required("Country is required"),
        pincode: Yup.string()
          .matches(/^[0-9]+$/, "Not a Valid Pincode")
          .required("Pincode is required"),
      }),
    }),
    Yup.object({
      accountHolderName: Yup.string().required(
        "Account holder name is required"
      ),
      bankName: Yup.string().required("Bank name is required"),
      accountNumber: Yup.string()
        .matches(/^[0-9]+$/, "Not a valid account number")
        .required("Account number is required"),
      ifscCode: Yup.string()
        .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Not a valid IFSC code")
        .required("IFSC code is required"),
      accountType: Yup.string().required("Account type is required"),
      upiId: Yup.string().required("UPI ID is required"),
    }),
    Yup.object({
      panCardNumber: Yup.string()
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Not a valid PAN number")
        .required("PAN card number is required"),
      aadhaarNumber: Yup.string()
        .matches(/^[0-9]{12}$/, "Not a valid Aadhaar number")
        .required("Aadhaar number is required"),
      panCard: Yup.mixed().required("PAN card is required"),
      aadhaarCard: Yup.mixed().required("Aadhaar card is required"),
      passportOrVoterId: Yup.mixed().nullable(),
      cancelledCheque: Yup.mixed().nullable(),
    }),
    Yup.object({
      referralCode: Yup.string(),
      termsAccepted: Yup.boolean().oneOf(
        [true],
        "You must accept the Terms and Conditions"
      ),
      declarationAccepted: Yup.boolean().oneOf(
        [true],
        "You must accept the Declaration"
      ),
    }),
  ];

  const initialSignupValues = {
    firstname: "",
    lastname: "",
    dob: "",
    gender: "",
    email: "",
    mobile: "",
    profilePhoto: null,
    address: {
      address_line: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
    referralCode: "",
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    accountType: "",
    upiId: "",
    panCardNumber: "",
    aadhaarNumber: "",
    panCard: null,
    aadhaarCard: null,
    passportOrVoterId: null,
    cancelledCheque: null,
    packageId: "",
    termsAccepted: false,
    declarationAccepted: false,
  };

  const steps = [
    "Personal Information",
    "Bank Details",
    "KYC Documents",
    "Reference",
  ];

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      dialogClassName="modal-90w md:modal-lg"
    >
      <Modal.Body className="p-4 md:p-6">
        <div className="relative bg-white rounded-lg">
          <button
            onClick={onHide}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>
          {!isRegistered && (
            <div className="text-center mb-4">
              <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                Member Sign Up
              </h3>
              <p className="text-gray-500 text-xs md:text-sm">
                Fill in your details to sign up as a Member
              </p>
            </div>
          )}
          {isRegistered ? (
            <div className="text-center py-6">
              <h2 className="text-lg md:text-xl font-bold text-green-600">
                Registration Successful
              </h2>
              <svg
                className="w-10 h-10 mx-auto text-green-600 mt-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          ) : (
            <>
              <Formik
                initialValues={initialSignupValues}
                // validationSchema={signupSchemas[currentStep]}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  if (currentStep < steps.length - 1) {
                    setCurrentStep((prev) => prev + 1);
                    setSubmitting(false);
                  } else {
                    try {
                      const formData = new FormData();
                      formData.append("firstname", values.firstname);
                      formData.append("lastname", values.lastname);
                      formData.append("dob", values.dob);
                      formData.append("gender", values.gender);
                      formData.append("email", values.email);
                      formData.append("mobile", values.mobile);
                      formData.append(
                        "address.address_line",
                        values.address.address_line
                      );
                      formData.append("address.city", values.address.city);
                      formData.append("address.state", values.address.state);
                      formData.append(
                        "address.country",
                        values.address.country
                      );
                      formData.append(
                        "address.pincode",
                        values.address.pincode
                      );
                      formData.append("referralCode", values.referralCode);
                      formData.append("profilePhoto", values.profilePhoto);
                      formData.append(
                        "accountHolderName",
                        values.accountHolderName
                      );
                      formData.append("bankName", values.bankName);
                      formData.append("accountNumber", values.accountNumber);
                      formData.append("ifscCode", values.ifscCode);
                      formData.append("accountType", values.accountType);
                      formData.append("upiId", values.upiId);
                      formData.append("panCardNumber", values.panCardNumber);
                      formData.append("aadhaarNumber", values.aadhaarNumber);
                      formData.append("panCard", values.panCard);
                      formData.append("aadhaarCard", values.aadhaarCard);
                      formData.append(
                        "passportOrVoterId",
                        values.passportOrVoterId
                      );
                      formData.append(
                        "cancelledCheque",
                        values.cancelledCheque
                      );
                      formData.append("userType", "member");

                      await dispatch(registerUser(formData)).unwrap();
                      setIsRegistered(true);
                      resetForm();
                      setTimeout(() => {
                        onHide();
                        toast.success(
                          "Registration successful! Please sign in."
                        );
                      }, 2000);
                    } catch (error) {
                      console.error("Registration error:", error);
                      toast.error(
                        error.response?.data?.message || "Registration failed"
                      );
                    } finally {
                      setSubmitting(false);
                    }
                  }
                }}
              >
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form className="flex flex-col gap-4">
                    {currentStep === 0 && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-10 gap-4">
                          <div className="md:col-span-2 flex justify-center">
                            <div
                              className="w-24 h-24 md:w-32 md:h-32 border-2 border-gray-300 rounded-md overflow-hidden cursor-pointer"
                              onClick={() =>
                                document.getElementById("profilePhoto").click()
                              }
                            >
                              {previewImage ? (
                                <img
                                  src={previewImage}
                                  alt="Profile Preview"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                  <span className="text-gray-500 text-sm">
                                    Upload Profile
                                  </span>
                                </div>
                              )}
                            </div>
                            <input
                              type="file"
                              id="profilePhoto"
                              className="hidden"
                              accept="image/*"
                              onChange={(event) => {
                                const file = event.target.files[0];
                                setFieldValue("profilePhoto", file);
                                if (file) {
                                  setPreviewImage(URL.createObjectURL(file));
                                } else {
                                  setPreviewImage(null);
                                }
                              }}
                            />
                          </div>
                          <div className="md:col-span-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  First Name
                                </label>
                                <Field
                                  type="text"
                                  name="firstname"
                                  placeholder="Enter First Name"
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                                <ErrorMessage
                                  name="firstname"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  Last Name
                                </label>
                                <Field
                                  type="text"
                                  name="lastname"
                                  placeholder="Enter Last Name"
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                                <ErrorMessage
                                  name="lastname"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <ErrorMessage
                          name="profilePhoto"
                          component="div"
                          className="text-red-500 text-xs text-center -mt-2 mb-2"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Date of Birth
                            </label>
                            <Field
                              type="date"
                              name="dob"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                              max={maxDate18.toISOString().split("T")[0]}
                            />
                            <ErrorMessage
                              name="dob"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Gender
                            </label>
                            <Field
                              as="select"
                              name="gender"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            >
                              <option value="">Select Gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </Field>
                            <ErrorMessage
                              name="gender"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Email
                            </label>
                            <Field
                              type="text"
                              name="email"
                              placeholder="Enter Email"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="email"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Phone Number
                            </label>
                            <Field
                              type="text"
                              name="mobile"
                              placeholder="Enter Phone Number"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="mobile"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm md:text-base font-semibold">
                            Street Address
                          </label>
                          <Field
                            type="text"
                            name="address.address_line"
                            placeholder="Enter Street Address"
                            className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                          />
                          <ErrorMessage
                            name="address.address_line"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              City
                            </label>
                            <Field
                              type="text"
                              name="address.city"
                              placeholder="Enter City"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="address.city"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              State
                            </label>
                            <Field
                              type="text"
                              name="address.state"
                              placeholder="Enter State"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="address.state"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Country
                            </label>
                            <Field
                              type="text"
                              name="address.country"
                              placeholder="Enter Country"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="address.country"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Pincode
                            </label>
                            <Field
                              type="text"
                              name="address.pincode"
                              placeholder="Enter Pincode"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="address.pincode"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {currentStep === 1 && (
                      <>
                        <h2 className="text-lg md:text-xl font-medium">
                          Bank Details
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Account Holder Name
                            </label>
                            <Field
                              type="text"
                              name="accountHolderName"
                              placeholder="Enter Account Holder Name"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="accountHolderName"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Bank Name
                            </label>
                            <Field
                              type="text"
                              name="bankName"
                              placeholder="Enter Bank Name"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="bankName"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Account Number
                            </label>
                            <Field
                              type="text"
                              name="accountNumber"
                              placeholder="Enter Account Number"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="accountNumber"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              IFSC Code
                            </label>
                            <Field
                              type="text"
                              name="ifscCode"
                              placeholder="Enter IFSC Code"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="ifscCode"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Account Type
                            </label>
                            <Field
                              as="select"
                              name="accountType"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            >
                              <option value="">Select Account Type</option>
                              <option value="Savings">Savings</option>
                              <option value="Current">Current</option>
                            </Field>
                            <ErrorMessage
                              name="accountType"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              UPI ID
                            </label>
                            <Field
                              type="text"
                              name="upiId"
                              placeholder="Enter UPI ID"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="upiId"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {currentStep === 2 && (
                      <>
                        <h2 className="text-lg md:text-xl font-medium">
                          KYC Documents
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              PAN Card Number
                            </label>
                            <Field
                              type="text"
                              name="panCardNumber"
                              placeholder="Enter PAN Card Number"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="panCardNumber"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Aadhaar Number
                            </label>
                            <Field
                              type="text"
                              name="aadhaarNumber"
                              placeholder="Enter Aadhaar Number"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="aadhaarNumber"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Upload PAN Card
                            </label>
                            <button
                              type="button"
                              className="w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm"
                              onClick={() =>
                                document.getElementById("panCard").click()
                              }
                            >
                              Upload
                            </button>
                            <input
                              type="file"
                              id="panCard"
                              className="hidden"
                              accept="image/*,application/pdf"
                              onChange={(event) => {
                                setFieldValue("panCard", event.target.files[0]);
                              }}
                            />
                            {values.panCard && (
                              <div className="mt-1 text-xs text-green-600">
                                {values.panCard.name || "File uploaded"}
                              </div>
                            )}
                            <ErrorMessage
                              name="panCard"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Upload Aadhaar Card
                            </label>
                            <button
                              type="button"
                              className="w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm"
                              onClick={() =>
                                document.getElementById("aadhaarCard").click()
                              }
                            >
                              Upload
                            </button>
                            <input
                              type="file"
                              id="aadhaarCard"
                              className="hidden"
                              accept="image/*,application/pdf"
                              onChange={(event) => {
                                setFieldValue(
                                  "aadhaarCard",
                                  event.target.files[0]
                                );
                              }}
                            />
                            {values.aadhaarCard && (
                              <div className="mt-1 text-xs text-green-600">
                                {values.aadhaarCard.name || "File uploaded"}
                              </div>
                            )}
                            <ErrorMessage
                              name="aadhaarCard"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Upload Passport or Voter ID
                            </label>
                            <button
                              type="button"
                              className="w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm"
                              onClick={() =>
                                document
                                  .getElementById("passportOrVoterId")
                                  .click()
                              }
                            >
                              Upload
                            </button>
                            <input
                              type="file"
                              id="passportOrVoterId"
                              className="hidden"
                              accept="image/*,application/pdf"
                              onChange={(event) => {
                                setFieldValue(
                                  "passportOrVoterId",
                                  event.target.files[0]
                                );
                              }}
                            />
                            {values.passportOrVoterId && (
                              <div className="mt-1 text-xs text-green-600">
                                {values.passportOrVoterId.name ||
                                  "File uploaded"}
                              </div>
                            )}
                            <ErrorMessage
                              name="passportOrVoterId"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Upload Cancelled Cheque
                            </label>
                            <button
                              type="button"
                              className="w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm"
                              onClick={() =>
                                document
                                  .getElementById("cancelledCheque")
                                  .click()
                              }
                            >
                              Upload
                            </button>
                            <input
                              type="file"
                              id="cancelledCheque"
                              className="hidden"
                              accept="image/*,application/pdf"
                              onChange={(event) => {
                                setFieldValue(
                                  "cancelledCheque",
                                  event.target.files[0]
                                );
                              }}
                            />
                            {values.cancelledCheque && (
                              <div className="mt-1 text-xs text-green-600">
                                {values.cancelledCheque.name || "File uploaded"}
                              </div>
                            )}
                            <ErrorMessage
                              name="cancelledCheque"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {currentStep === 3 && (
                      <>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Referral Code
                            </label>
                            <Field
                              type="text"
                              name="referralCode"
                              placeholder="Enter Referral Code"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                              onChange={(e) => {
                                setFieldValue("referralCode", e.target.value);
                                debouncedFetchReferredUserData(e.target.value);
                              }}
                            />
                            <ErrorMessage
                              name="referralCode"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        {referredUserData && (
                          <>
                            <h2 className="text-lg md:text-xl font-medium">
                              Upline Information
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  Full Name
                                </label>
                                <input
                                  type="text"
                                  value={referredUserData.firstname || ""}
                                  readOnly
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                              </div>
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  Phone Number
                                </label>
                                <input
                                  type="text"
                                  value={referredUserData.mobile || ""}
                                  readOnly
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                              </div>
                            </div>
                          </>
                        )}
                        <div className="mt-4">
                          <h2 className="text-lg md:text-xl font-medium">
                            Terms and Conditions
                          </h2>
                          <div className="max-h-40 overflow-y-auto border border-gray-300 p-3 rounded-md bg-gray-50 text-sm">
                            <p>
                              By enrolling as a member and network marketing
                              partner of LifeOnPlus, you agree to the following
                              terms:
                            </p>
                            <ol className="list-decimal ml-4 mt-2">
                              <li>
                                <strong>Eligibility:</strong> You must be 18+
                                years of age with valid KYC documents.
                              </li>
                              <li>
                                <strong>Referral Income:</strong> Income is
                                earned only upon successful sale or onboarding as
                                per the business plan and is subject to
                                applicable deductions (TDS, service fee, GST).
                              </li>
                              <li>
                                <strong>Commission Withdrawal:</strong>
                                <ul className="list-disc ml-4">
                                  <li>Minimum withdrawal amount: ₹200</li>
                                  <li>
                                    TDS of 2% (20% if PAN not provided), service
                                    charge of 10% + GST (18%)
                                  </li>
                                  <li>
                                    Wallet retention policy applies (e.g., 40%
                                    wallet holding)
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <strong>Compliance:</strong> You are responsible
                                for ensuring ethical promotion of LifeOnPlus
                                services and not making false claims.
                              </li>
                              <li>
                                <strong>Termination:</strong> LifeOnPlus reserves
                                the right to terminate a member’s account in case
                                of fraudulent activity, misrepresentation, or
                                breach of company policies.
                              </li>
                              <li>
                                <strong>Data Usage:</strong> Your personal and
                                banking data will be securely stored and only
                                used for internal business and payout purposes.
                              </li>
                              <li>
                                <strong>Refund Policy:</strong> Refunds
                                applicable only if 10x business is done within
                                120 hours (for specific ranks).
                              </li>
                              <li>
                                <strong>Non-Transferable:</strong> Your
                                membership is personal and cannot be transferred
                                or sold.
                              </li>
                              <li>
                                <strong>Business Opportunity Disclaimer:</strong>{" "}
                                Earnings depend on your effort, sales
                                performance, and team activity. LifeOnPlus does
                                not guarantee fixed returns.
                              </li>
                              <li>
                                <strong>Code of Conduct:</strong> As a
                                representative of LifeOnPlus, you are expected to
                                maintain professionalism and comply with all
                                legal norms.
                              </li>
                            </ol>
                          </div>
                          <div className="mt-2 flex items-center">
                            <Field
                              type="checkbox"
                              name="termsAccepted"
                              className="h-4 w-4 text-[#00C70D] border-gray-300 rounded cursor-pointer"
                            />
                            <label className="ml-2 text-sm">
                              I accept the Terms and Conditions
                            </label>
                          </div>
                          <ErrorMessage
                            name="termsAccepted"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div className="mt-4">
                          <h2 className="text-lg md:text-xl font-medium">
                            Declaration
                          </h2>
                          <div className="border border-gray-300 p-3 rounded-md bg-gray-50 text-sm">
                            <p>
                              I, {values.firstname || "_________"} {values.lastname || ""}, declare that
                              the information provided is true and correct. I
                              have read and understood the Terms & Conditions of
                              LifeOnPlus and agree to abide by them.
                            </p>
                          </div>
                          <div className="mt-2 flex items-center">
                            <Field
                              type="checkbox"
                              name="declarationAccepted"
                              className="h-4 w-4 text-[#00C70D] border-gray-300 rounded cursor-pointer"
                            />
                            <label className="ml-2 text-sm">
                              I accept the Declaration
                            </label>
                          </div>
                          <ErrorMessage
                            name="declarationAccepted"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                      </>
                    )}
                    <div className="mt-6 flex justify-center gap-3">
                      {currentStep > 0 && (
                        <button
                          type="button"
                          className="w-28 md:w-36 py-2 bg-gray-300 text-gray-800 font-semibold text-sm md:text-base rounded-md shadow-md hover:bg-gray-400"
                          onClick={() => setCurrentStep((prev) => prev - 1)}
                        >
                          Back
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-28 md:w-36 py-2 bg-[#00C70D] text-white font-semibold text-sm md:text-base rounded-md shadow-md flex items-center justify-center ${
                          isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 md:h-5 md:w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Loading...
                          </>
                        ) : currentStep < steps.length - 1 ? (
                          "Continue"
                        ) : (
                          "Register"
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MemberSignUp;